import { Empty } from '@pinpointhq/thumbtack';
import axios from 'axios';
import { debounce } from 'lodash';
import * as React from 'react';
import { LoadingSpinner } from '../spinner/LoadingSpinner';
import { getCsrfToken } from '../utilities/csrfToken';

function Error({ error }: { error: string }) {
  return (
    <Empty description="Unable to generate preview">
      <div className="mar-t-2">{error}</div>
    </Empty>
  );
}

function IFrame({ htmlContent }: { htmlContent?: string }) {
  const iframe = React.useRef<HTMLIFrameElement>();

  function setHeight() {
    const height = iframe.current.contentWindow.document.body.scrollHeight;
    iframe.current.height = height.toString();
  }

  function updateIframeContent() {
    const { document } = iframe.current.contentWindow;
    document.open();
    document.write(htmlContent || '');
    document.close();
  }

  React.useEffect(() => {
    iframe.current.addEventListener('load', setHeight);
    updateIframeContent();

    return () => {
      iframe.current.removeEventListener('load', setHeight);
    };
  }, [htmlContent]);

  return (
    <iframe
      title="preview"
      ref={iframe}
      seamless={true}
      style={{ border: 'none', width: '100%', minHeight: '640px' }}
    />
  );
}

interface IPreviewIframe {
  isBase64Pdf?: boolean;
  url: string;
  params?: object;
  setIsLoading?: (status: boolean) => any;
}

export function PreviewIframe({ url, params, setIsLoading, isBase64Pdf = false }: IPreviewIframe) {
  const [error, setError] = React.useState<string>(null);
  const [htmlContent, setHtmlContent] = React.useState<string>(null);

  function fetchData(fetchUrl: string, fetchParams: object) {
    if (setIsLoading) setIsLoading(true);

    axios
      .post<string>(fetchUrl, { ...fetchParams, authenticity_token: getCsrfToken() })
      .then((response) => {
        setError(null);
        setHtmlContent(response.data);
      })
      .catch((e) => {
        /**
         * Axios changed their typings and it's indicated that this error is not necessarily an AxiosError instance
         * so we're going to leave it untyped as it makes my dependabot initiated Axios update a little easier
         * @see https://github.com/axios/axios/issues/4716
         * @see https://axios-http.com/docs/handling_errors
         */
        if (e.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (
            e.response.data &&
            e.response.data.errors &&
            Array.isArray(e.response.data.errors) &&
            e.response.data.errors.length > 0 &&
            e.response.data.errors[0].title
          ) {
            const errors = e.response.data.errors as { title: string }[];
            setError(errors.map((err) => err.title).join(', '));
          } else {
            setError('There was a problem with the server response');
          }
        } else if (e.request) {
          // The request was made but no response was received
          // `e.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          setError('There was a problem with the request');
        } else {
          // Something happened in setting up the request that triggered an Error
          setError('There was a problem with the request');
        }
      })
      .then(() => {
        if (setIsLoading) setIsLoading(false);
      });
  }

  const debouncedFetchData = React.useMemo(() => debounce(fetchData, 500), []);

  React.useEffect(() => {
    debouncedFetchData(url, params);
  }, [url, params]);

  if (error) return <Error {...{ error }} />;

  return isBase64Pdf ? <Base64PdfIframe {...{ htmlContent }} /> : <IFrame {...{ htmlContent }} />;
}

function Base64PdfIframe({ htmlContent }: { htmlContent: string }) {
  /**
   * If we don't present a loading spinner then we actually render a
   * PDF that says 'could not load the PDF' or something else equally broken
   */
  if (!htmlContent) {
    return (
      <div style={{ background: '#F6F6F6' }}>
        <LoadingSpinner height="640px" />
      </div>
    );
  }
  return (
    <iframe
      title="PDF preview"
      src={`data:application/pdf;base64, ${htmlContent}`}
      seamless={true}
      style={{ border: 'none', width: '100%', minHeight: '640px' }}
    />
  );
}

export default PreviewIframe;
